/**
 * MobileBottomNav — sticky-bottom navigation on mobile only.
 *
 * A native-feeling app-tabbar: Home / News / Roster / Film Room / Search.
 * Hidden ≥md (lg feels too high; tablet users prefer the top header). Sticks
 * to the visual viewport bottom with `safe-area-inset` so it clears the iOS
 * home indicator.
 *
 * Active tab is derived from the current pathname prefix.
 */
import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, Newspaper, Gauge, Film, Search, X } from "lucide-react";

const TABS = [
  { to: "/", label: "Home", icon: Home, match: (p) => p === "/" },
  { to: "/news", label: "News", icon: Newspaper, match: (p) => p.startsWith("/news") || p === "/breaking-news" || p === "/analysis" || p === "/offseason" },
  { to: "/command-center", label: "Command", icon: Gauge, match: (p) => p.startsWith("/command-center") || p.startsWith("/roster") || p.startsWith("/player") },
  { to: "/film-room", label: "Film", icon: Film, match: (p) => p.startsWith("/film-room") },
  { to: "search", label: "Search", icon: Search, match: (p) => false }, // opens overlay
];

export default function MobileBottomNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");

  // Never render on the admin routes (its own layout) or live embeds.
  if (pathname.startsWith("/admin") || pathname.startsWith("/live")) return null;

  const submitSearch = (e) => {
    e.preventDefault();
    const query = q.trim();
    if (!query) return;
    setSearchOpen(false);
    setQ("");
    // /news route already supports ?q= via the client — falls back to
    // scanning headlines. Keep the intent obvious in the URL.
    navigate(`/news?q=${encodeURIComponent(query)}`);
  };

  return (
    <>
      <nav
        aria-label="Primary mobile navigation"
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-white/10 bg-titans-void/95 backdrop-blur"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <ul className="grid grid-cols-5">
          {TABS.map(({ to, label, icon: Icon, match }) => {
            if (to === "search") {
              const active = searchOpen;
              return (
                <li key="search">
                  <button
                    type="button"
                    onClick={() => setSearchOpen(true)}
                    data-testid="mobile-nav-search"
                    className={`w-full flex flex-col items-center gap-0.5 pt-2 pb-2 transition ${
                      active ? "text-titans-blue" : "text-titans-silver hover:text-white"
                    }`}
                  >
                    <Icon size={20} />
                    <span className="text-[10px] font-cond uppercase tracking-wider">{label}</span>
                  </button>
                </li>
              );
            }
            const isActive = match(pathname);
            return (
              <li key={to}>
                <NavLink
                  to={to}
                  data-testid={`mobile-nav-${label.toLowerCase()}`}
                  className={`flex flex-col items-center gap-0.5 pt-2 pb-2 transition ${
                    isActive ? "text-titans-blue" : "text-titans-silver hover:text-white"
                  }`}
                >
                  <Icon size={20} />
                  <span className="text-[10px] font-cond uppercase tracking-wider">{label}</span>
                  {isActive && (
                    <span className="absolute -top-px w-8 h-0.5 bg-titans-blue" aria-hidden="true" />
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {searchOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 bg-titans-void/95 backdrop-blur flex flex-col p-5"
          data-testid="mobile-search-overlay"
          role="dialog"
          aria-label="Search"
        >
          <button
            type="button"
            onClick={() => setSearchOpen(false)}
            className="self-end text-titans-silver hover:text-white p-2"
            aria-label="Close search"
          >
            <X size={22} />
          </button>
          <form onSubmit={submitSearch} className="mt-8">
            <label htmlFor="mobile-search-input" className="block font-cond uppercase tracking-wider text-[11px] text-titans-blue mb-2">Search The Titan Standard</label>
            <input
              id="mobile-search-input"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Try 'depth chart' or a player name"
              className="w-full bg-transparent border-b-2 border-titans-blue text-white text-2xl md:text-3xl font-display uppercase pb-3 focus:outline-none placeholder:text-titans-silver/50"
              data-testid="mobile-search-input"
            />
            <button
              type="submit"
              className="mt-8 w-full bg-titans-red hover:bg-titans-red-hover text-white font-cond uppercase tracking-wider py-3"
              data-testid="mobile-search-submit"
            >
              Search
            </button>
          </form>
        </div>
      )}

      {/* Reserve space at the bottom of every page so content isn't hidden
          behind the fixed nav. Only on mobile. */}
      <div className="md:hidden h-16" aria-hidden="true" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }} />
    </>
  );
}
